// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { formatToPar } from '@/features/statistics/components/Charts';
import { EmptyHint } from '@/features/statistics/components/StatPieces';
import { formatMerits, formatPosition, formatShortDate } from '@/features/statistics/formatting';
import { useMyRounds } from '@/features/my-rounds/useMyRounds';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

/** Rounds the player has played, with their result and a link to each scorecard. */
export default function MyRoundsScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { rounds, loading, error, reload } = useMyRounds({ apiBaseUrl, token: user?.token });

  const addRoundButton = (
    <GoffyButton
      label={t('myRounds.addRound')}
      onPress={() => router.push('/log-round')}
      testID="my-rounds-add"
    />
  );

  if (!user?.token) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{t('myRounds.signedOut')}</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{t(`myRounds.${error}`)}</Text>
        <GoffyButton label={t('myRounds.retry')} onPress={reload} testID="my-rounds-retry" />
      </View>
    );
  }

  if (loading && !rounds) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color={appColors.accent.primary} />
        <Text style={styles.message}>{t('myRounds.loading')}</Text>
      </View>
    );
  }

  if (!rounds || rounds.length === 0) {
    return (
      <View style={styles.centered}>
        {addRoundButton}
        <EmptyHint text={t('myRounds.empty')} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <View style={styles.header}>{addRoundButton}</View>
      {rounds.map((round) => (
        <Pressable
          key={round.roundId}
          style={styles.row}
          testID={`my-round-${round.roundId}`}
          accessibilityRole="button"
          onPress={() => router.push(`/round/${round.roundId}/scorecard`)}
        >
          <View style={styles.rowHeader}>
            <Text style={styles.date}>{formatShortDate(round.playDate, round.playedAtUtc)}</Text>
            <Text style={styles.course} numberOfLines={1}>
              {round.courseName}
            </Text>
            {round.status.toLowerCase() !== 'finished' ? (
              <Text style={styles.badge}>{t('myRounds.inProgress')}</Text>
            ) : null}
          </View>
          <Text style={styles.tour} numberOfLines={1}>
            {round.tourName}
          </Text>
          <View style={styles.rowFooter}>
            <Text style={styles.score}>
              {t('myRounds.roundScore', { gross: round.grossStrokes, toPar: formatToPar(round.toPar) })}
            </Text>
            {round.position !== null && round.fieldSize > 1 ? (
              <Text style={[styles.position, round.position === 1 && styles.positionGold]}>
                {t('myRounds.positionOf', { position: formatPosition(round.position), total: round.fieldSize })}
              </Text>
            ) : (
              <Text style={styles.positionMuted}>{formatMerits(round.meritPoints)}</Text>
            )}
          </View>
        </Pressable>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 12,
    gap: 8,
  },
  header: {
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  message: {
    fontSize: 14,
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  row: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 12,
    gap: 4,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  date: {
    fontSize: 12,
    fontWeight: '700',
    color: appColors.text.muted,
  },
  course: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.heading,
  },
  badge: {
    fontSize: 10.5,
    fontWeight: '700',
    color: appColors.accent.primary,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  tour: {
    fontSize: 12,
    color: appColors.text.muted,
  },
  rowFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  score: {
    fontSize: 13,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  position: {
    fontSize: 12.5,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  positionGold: {
    color: appColors.accent.gold,
  },
  positionMuted: {
    fontSize: 12.5,
    fontWeight: '600',
    color: appColors.text.muted,
  },
});
